// Walks every cursor page of a Storefront connection. The next page is
// requested as soon as its cursor is known, so it loads while onPage handles
// the current one.
//
// - request(after) resolves to a raw response (or anything getConnection reads)
// - getConnection(response) returns the connection ({ nodes | edges, pageInfo })
//   or null, which ends the walk as incomplete
// - onPage(nodes, { pageCount, hasNextPage }) may return false to stop early
//
// Resolves { pageCount, completed, truncated }: completed is false when a page
// was missing or onPage stopped the walk; truncated is true when maxPages cut
// off pages that still existed.
export async function forEachConnectionPage({ request, getConnection, onPage, maxPages = Infinity }) {
  let pageCount = 0;
  let pending = request(null);

  while (pending) {
    const response = await pending;

    if (response?.errors?.length) {
      throw new Error(`Storefront request failed: ${JSON.stringify(response.errors)}`);
    }

    const connection = getConnection(response);

    if (!connection) {
      return { pageCount, completed: false, truncated: false };
    }

    pageCount += 1;

    const hasMore = Boolean(connection.pageInfo?.hasNextPage && connection.pageInfo.endCursor);
    const truncated = hasMore && pageCount >= maxPages;
    const nodes = connection.nodes || (connection.edges || []).map((edge) => edge?.node).filter(Boolean);

    pending = hasMore && !truncated ? request(connection.pageInfo.endCursor) : null;

    if ((await onPage?.(nodes, { pageCount, hasNextPage: Boolean(pending) })) === false) {
      return { pageCount, completed: false, truncated: false };
    }

    if (truncated) {
      return { pageCount, completed: true, truncated: true };
    }
  }

  return { pageCount, completed: true, truncated: false };
}
